import { useEffect, useState } from "react";
import { createFileRoute, Outlet, useRouterState } from "@tanstack/react-router";
import { Settle } from "@/ui/Notice";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { sessionKey, useSession } from "@/lib/session";
import { Panel } from "@/ui/Panel";
import { Head } from "@/ui/frame/Page";
import { Button } from "@/ui/Button";
import { Field, Input } from "@/ui/Field";
import { FormError } from "@/features/auth/AuthCard";
import { useToast } from "@/ui/Toast";
import { absoluteTime } from "@/lib/format";

export const Route = createFileRoute("/_app/account")({
  component: AccountScreen,
});

function AccountScreen() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  // The security page is a child route; the profile form is this route's own
  // content and must not render underneath it.
  if (pathname !== "/account") return <Settle>
        <Outlet />
      </Settle>;

  return <ProfileScreen />;
}

function ProfileScreen() {
  const { user } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [displayName, setDisplayName] = useState("");

  useEffect(() => {
    if (user) setDisplayName(user.display_name);
  }, [user]);

  const save = useMutation({
    mutationFn: () => api.patch<{ user: unknown }>("/me", { display_name: displayName }),
    onSuccess: async () => {
      toast.success("Profile updated");
      await queryClient.invalidateQueries({ queryKey: sessionKey });
    },
  });

  if (!user) return null;
  const unchanged = displayName.trim() === user.display_name;

  return (
    <div className="mx-auto flex max-w-[42rem] flex-col gap-6">
      <Head title="Profile" description="How you appear to colleagues in your organisations." />

      {/* No heading: the page is called Profile and this is the profile.
          And bounded, because a box for a person's name does not get better
          by being eleven hundred pixels wide — a field is a measure of what
          goes in it. Bounded and centred: pinned to the left it was 672
          pixels of form with 448 of nothing beside it, which is the single
          worst case of this in the product. */}
      <Panel
        footer={
          <>
            <span className="text-[13px] text-[var(--text-muted)]">
              Joined {absoluteTime(user.created_at)}
            </span>
            <Button
              tone="primary"
              size="dense"
              busy={save.isPending}
              disabled={unchanged}
              onClick={() => save.mutate()}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Field label="Name">
            {({ id }) => (
              <Input
                id={id}
                value={displayName}
                maxLength={80}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            )}
          </Field>

          <Field
            label="Email address"
            hint="Changing your address is not available yet. It is the address your invitations and security notices go to."
          >
            {({ id, describedBy }) => (
              <Input id={id} value={user.email} disabled readOnly aria-describedby={describedBy} />
            )}
          </Field>

          {save.error ? <FormError error={save.error} /> : null}
        </div>
      </Panel>
    </div>
  );
}
